import '@/assets/styles/app.css';

import { createApp, markRaw } from "vue";
import router from "./router";
import { createPinia } from "pinia";
import echo from "./echo.js";
import App from "./App.vue";
import { initializeTheme } from './composables/useAppearance';
import { registerSW } from './registerSW.js';
import onlineCheckService from '@/services/onlineCheckService.js';
import { ensureOfflineCacheMatchesBuild, initStorageLifecycle } from '@/services/storageLifecycleService.js';
import { vOffline, vOnline } from '@/directives/onlineOffline.js';
import { useAuthStore } from '@/stores/auth-store.js';
import { useSaleStore } from '@/stores/sale-store.js';

window.Echo = echo;

const app = createApp(App);

const pinia = createPinia();

/* router injection into pinia */
pinia.use(({ store }) => {
    store.$router = markRaw(router);
});

app.use(pinia);

/* directives */
app.directive('online', vOnline);
app.directive('offline', vOffline);

async function bootstrap() {
    // Ensure Pinia mirrors connectivity before the bounded initial ping.
    useAuthStore();

    // Resolve backend reachability before router guards or mounted components
    // can issue POST-shaped reads. The health check is bounded by the service.
    try {
        await onlineCheckService.init();
    } catch (error) {
        console.warn('[bootstrap] initial connectivity check failed', error);
        onlineCheckService.setConnectivityStatus('offline', { fromEvent: 'bootstrap.error' });
    }
    // The probe above carries the deployment stamp, so this is the first moment
    // the app can tell that its IndexedDB caches were filled by a build that is
    // no longer running. It has to be settled *before* anything reads or
    // reseeds them: App.vue converges the list caches on the login watch during
    // mount, and a clear landing after that would wipe the reseed it just paid
    // for. Awaited rather than fired, and non-fatal — an offline boot has no
    // stamp to compare and is expected to skip.
    try {
        await ensureOfflineCacheMatchesBuild();
    } catch (error) {
        console.warn('[bootstrap] offline cache build reconcile failed', error);
    }

    app.use(router);
    await router.isReady();

    const saleStore = useSaleStore();
    try {
        await saleStore.loadFromIDB();
    } catch (error) {
        // A corrupt/unavailable draft cache must not prevent the shell and
        // recovery UI from mounting.
        console.warn('[bootstrap] POS draft restore failed', error);
    }

    app.mount("#app");

    try {
        initStorageLifecycle();
    } catch (error) {
        console.warn('[bootstrap] storage lifecycle initialization failed', error);
    }
    try {
        registerSW();
    } catch (error) {
        console.warn('[bootstrap] service-worker registration failed', error);
    }
    initializeTheme();
}

bootstrap().catch((error) => {
    // The app should already be mounted unless router installation itself
    // failed; keep a visible diagnostic instead of an unexplained blank page.
    console.error('[bootstrap] fatal initialization error', error);
    const root = document.getElementById('app');
    if (root && !root.hasChildNodes()) {
        root.textContent = 'Inventree could not start. Please reload the page.';
    }
});
